<template>
  <div class="page-shell">
    <PageBanner
      eyebrow="PRODUCT DETAIL"
      background-image="/page/products-banner.jpg"
      :title="page.title"
      :subtitle="page.intro"
    />

    <section class="section">
      <div class="container product-layout">
        <aside v-reveal class="side-nav">
          <h3>{{ $pick(sideTitle) }}</h3>
          <router-link
            v-for="item in productCategories"
            :key="item.path"
            :class="{ active: $route.path === item.path }"
            :to="item.path"
          >
            {{ $pick(item.label) }}
          </router-link>
        </aside>

        <div v-reveal class="content-pane">
          <div class="breadcrumb">{{ $pick(breadcrumb) }}</div>
          <div class="gallery-head">
            <div>
              <span>PRODUCT GALLERY</span>
              <h2>{{ $pick(page.title) }}</h2>
            </div>
            <p>{{ $pick(page.galleryText || page.intro) }}</p>
          </div>

          <div class="product-gallery">
            <router-link
              v-for="image in productImages"
              :key="image.src"
              class="product-card"
              :to="detailPath(image)"
            >
              <img :src="image.src" :alt="$pick(image.title)" loading="lazy" />
              <div>
                <h3>{{ $pick(image.title) }}</h3>
                <p v-if="image.summary">{{ $pick(image.summary) }}</p>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <section class="section section-muted">
      <div class="container">
<!--        <SectionTitle eyebrow="FEATURES" :title="featureTitle" :description="featureDescription" />-->
        <SimpleCardList :items="featureItems" />
      </div>
    </section>
  </div>
</template>

<script>
import PageBanner from "../../components/common/PageBanner.vue";
import SectionTitle from "../../components/common/SectionTitle.vue";
import SimpleCardList from "../../components/common/SimpleCardList.vue";
import { productCategories, productPageMap } from "../../data/site";

export default {
  name: "ProductDetailBase",
  components: { PageBanner, SectionTitle, SimpleCardList },
  props: {
    routeKey: {
      type: String,
      required: true
    }
  },
  computed: {
    page() {
      return productPageMap[this.routeKey] || productPageMap["/products/oil-immersed"];
    },
    productCategories() {
      return productCategories;
    },
    productImages() {
      return this.page.images || [];
    },
    sideTitle() {
      return { "zh-CN": "产品中心", "en-US": "Products" };
    },
    breadcrumb() {
      return {
        "zh-CN": "首页 > 产品中心 > " + this.$pick(this.page.title),
        "en-US": "Home > Products > " + this.$pick(this.page.title)
      };
    },
    featureItems() {
      return this.page.cards;
    },
    featureTitle() {
      return {
        "zh-CN": this.$pick(this.page.title) + " 产品亮点",
        "en-US": this.$pick(this.page.title) + " Highlights"
      };
    },
    featureDescription() {
      return {
        "zh-CN": "结合产品应用场景整理核心优势，便于客户快速判断适配方向。",
        "en-US": "Key strengths are organized around practical application scenarios."
      };
    }
  },
  methods: {
    detailPath(image) {
      return image.slug ? `/products/detail/${image.slug}` : this.routeKey;
    }
  }
};
</script>

<style scoped>
.product-layout {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 28px;
  align-items: start;
}

.side-nav {
  padding: 24px;
  background: #fff;
  border: 1px solid var(--line);
  box-shadow: var(--shadow);
}

.side-nav h3 {
  margin: 0 0 18px;
  color: var(--brand-blue-deep);
  font-size: 20px;
}

.side-nav a {
  display: block;
  margin-bottom: 10px;
  padding: 12px 14px;
  background: #f5f8fc;
  color: #39495c;
}

.side-nav a.active,
.side-nav a:hover {
  background: var(--brand-blue);
  color: #fff;
}

.content-pane {
  padding: 28px 30px 32px;
  background: #fff;
  border: 1px solid var(--line);
  box-shadow: var(--shadow);
}

.breadcrumb {
  margin-bottom: 18px;
  color: #8a94a3;
  font-size: 13px;
}

.gallery-head {
  display: grid;
  grid-template-columns: minmax(220px, 0.8fr) minmax(280px, 1fr);
  gap: 24px;
  align-items: end;
  margin-bottom: 24px;
}

.gallery-head span {
  color: var(--brand-blue);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
}

.gallery-head h2 {
  margin: 8px 0 0;
  color: var(--brand-blue-deep);
  font-size: 30px;
}

.gallery-head p {
  margin: 0;
  color: #5f6976;
  line-height: 1.8;
}

.product-gallery {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.product-card {
  display: block;
  overflow: hidden;
  background: #f7f9fc;
  border: 1px solid var(--line);
  color: inherit;
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
}

.product-card:hover {
  transform: translateY(-4px);
  border-color: rgba(18, 95, 166, 0.32);
  box-shadow: 0 18px 42px rgba(10, 31, 58, 0.13);
}

.product-card img {
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  background: #eef3f8;
}

.product-card div {
  padding: 14px 16px 16px;
}

.product-card h3 {
  margin: 0;
  color: var(--brand-blue-deep);
  font-size: 16px;
  line-height: 1.45;
}

.product-card p {
  margin: 8px 0 0;
  color: #637083;
  font-size: 14px;
  line-height: 1.65;
}

.section-muted {
  background: #f5f8fc;
}

@media (max-width: 1100px) {
  .product-gallery {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .product-layout,
  .gallery-head {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 560px) {
  .side-nav,
  .content-pane {
    padding: 20px 18px;
  }

  .side-nav {
    display: grid;
    gap: 8px;
  }

  .side-nav h3 {
    margin-bottom: 8px;
    font-size: 18px;
  }

  .side-nav a {
    margin-bottom: 0;
    padding: 11px 12px;
  }

  .gallery-head h2 {
    font-size: 24px;
    line-height: 1.28;
  }

  .gallery-head p {
    font-size: 14px;
    line-height: 1.8;
  }

  .product-gallery {
    grid-template-columns: 1fr;
  }
}
</style>
